import { TablePreview } from '../components/TablePreview';
import { TablePreviewToggle } from '../components/TablePreviewToggle';
import { RestorationControls } from '../components/RestorationControls';
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  BALL_VALUES,
  COLORS,
  canPot,
  freeBallOffered,
  freeBallValue,
  isBallOnTable,
  isFreeBallPot,
  illegalRedAdjustmentReason,
  type BallColor,
  type PlayerIndex,
  type ScoreState,
} from '@aiscoreboard/contracts';
import { BALL_GLOSS, BALL_SWATCH, PALE_BALLS, ballKey } from '../lib/balls';
import { CalculatorIcon, CrownIcon, FoulIcon, UndoIcon } from '../components/Icons';
import { BreakHistory } from '../components/BreakHistory';
import { ScoreEntryDialog } from '../components/ScoreEntryDialog';
import { FrameWinnerDialog } from '../components/FrameWinnerDialog';
import { FoulFollowUp } from '../components/FoulFollowUp';
import { FullscreenToggle } from '../components/FullscreenToggle';
import { accentOf, frameVars } from '../lib/accent';
import { useT } from '../lib/i18n';
import { useMatch } from '../lib/useMatch';
import { useOperatorTimeout } from '../lib/useOperatorTimeout';

interface Props {
  matchId: string;
}

/**
 * A phone held at the table, driving a scoreboard across the room.
 *
 * The QR code on the scoreboard used to open the whole app, which was the
 * wrong answer to the right question: a phone that has just scanned a code is
 * in somebody's hand, at the table, between shots. It does not need a second
 * copy of a display built to be read from ten feet away —it needs the
 * buttons. So this screen is only the buttons, plus just enough score to be
 * sure you are driving the right match.
 *
 * There is no master and no slave. This posts to the same event log over the
 * same socket as the scoreboard, so both move together and either can undo the
 * other. The QR grants only this match's scoring controls, never account or
 * camera access, so a guest referee does not need to sign in.
 */
export function RemoteScreen({ matchId }: Props) {
  useOperatorTimeout(false);
  const t = useT();
  const { refresh, state, connected, error, notice, saving, savingSlow, dispatch, undoLast, finishFrame,
    frameWinnerRequired, cancelFrameWinner, returnToOffender, recordFoul } = useMatch(
    matchId,
    false,
  );
  const [scoreEntry, setScoreEntry] = useState<{ matchId: string; frameNumber: number; player: PlayerIndex } | null>(null);
  const [showTablePreview, setShowTablePreview] = useState(false);

  useEffect(() => {
    setScoreEntry(entry => entry && (entry.matchId !== state?.matchId
      || entry.frameNumber !== state?.frameNumber || state?.status !== 'in_progress') ? null : entry);
  }, [state?.matchId, state?.frameNumber, state?.status]);

  if (error && !state) {
    return (
      <main className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
        <h1 className="text-xl font-black">{t('match.unavailable')}</h1>
        <p className="max-w-xs text-sm text-white/60">{error}</p>
        <p className="font-mono text-xs text-white/30">{matchId}</p>
        <p className="text-xs text-white/40">{t('remote.scanAgain')}</p>
      </main>
    );
  }

  if (!state) {
    return (
      <main className="grid h-full place-items-center text-white/50">{t('remote.connecting')}</main>
    );
  }

  const live = state.status === 'in_progress';
  const statusMessage = error ?? notice ?? (savingSlow ? t('match.saving') : null);
  const exhausted = state.pointsAvailable === 0;
  const paused = state.paused;
  const pauseNotice =
    state.pauseMessage ??
    (state.pauseReason === 'waiting_for_respot'
      ? t('match.pausedWaitingRespot')
      : t('match.paused'));
  const locked = !live || exhausted || paused;
  const active = state.activePlayer;
  const canSwitch = live && !paused;

  const switchPlayer = async (): Promise<void> => {
    if (!live || paused) return;
    await dispatch({ type: 'shot.miss', source: 'manual', player: active });
  };

  const pot = (color: BallColor, count: number, freeBall = false): void => {
    void dispatch({
      type: 'shot.pot',
      source: 'manual',
      player: active,
      color,
      count,
      freeBall,
    });
  };

  const setFreeBall = (awarded: boolean): void => {
    void dispatch({ type: 'freeball.awarded', source: 'manual', player: active, awarded });
  };

  return (
    // Normal phone layouts stay within the viewport. Short windows can scroll
    // rather than hiding scoring controls below the bottom edge.
    //
    // Four cards, top to bottom, and nothing loose between them: who is
    // playing, what may be potted, whose turn it is, and how to take it back.
    // Every one of them is the same sheet of glass the scoreboard is built
    // from — a screen where some blocks are framed and some are bare reads as
    // half-finished, and the bare ones read as captions for whatever happens
    // to sit above them.
    <main className="relative flex h-full flex-col justify-between gap-2 overflow-y-auto p-3">
      {statusMessage && (
        <div
          role={error ? 'alert' : 'status'}
          className="hud-frame hud-frame-sm pointer-events-none absolute bottom-24 left-1/2 z-20
                     w-max max-w-[90%] -translate-x-1/2 px-4 py-2 text-center text-sm font-medium"
        >
          <span className={error ? 'text-sys-red' : 'text-white/85'}>{statusMessage}</span>
        </div>
      )}
      <header
        className="hud-frame hud-frame-sm grid shrink-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 px-2 py-1.5"
      >
        <div className="flex min-w-0 items-center gap-1.5">
          <span
            role="img"
            aria-label={connected ? t('remote.connected') : t('match.reconnecting')}
            title={connected ? t('remote.connected') : t('match.reconnecting')}
            className={`h-2 w-2 shrink-0 rounded-full ${connected ? 'bg-sys-green' : 'bg-sys-red'}`}
          />
        </div>
        <div data-remote-frames role="group"
          aria-label={t('remote.framesShort', { a: state.framesWon[0], b: state.framesWon[1] })}
          className="flex min-w-[4.5rem] flex-col items-center justify-center">
          <span aria-hidden className="num flex items-center justify-center gap-2 text-[2.5rem] font-bold leading-10 tabular-nums">
            <span data-frame-player="0" style={{ color: accentOf(0).hex }}>{state.framesWon[0]}</span>
            <span className="text-white/40">:</span>
            <span data-frame-player="1" style={{ color: accentOf(1).hex }}>{state.framesWon[1]}</span>
          </span>
        </div>
        <div className="flex min-w-0 flex-wrap items-center justify-end gap-1">
          {state.mode === 'assisted' && <TablePreviewToggle pressed={showTablePreview}
            onToggle={() => setShowTablePreview(value => !value)} />}
          <FullscreenToggle className="btn-ghost min-w-0 truncate px-2 py-2 text-[0.62rem] font-bold text-white/85" />
        </div>
      </header>

      <ScoreLine state={state} onTakeTable={canSwitch ? () => void switchPlayer() : undefined}
        onAddScore={player => setScoreEntry({ matchId, frameNumber: state.frameNumber, player })} />

      {/* What is left on the table: reds, points still available, and who is
          ahead by how much.
          It used to sit beside a block naming the ball that is on — which the
          pad below already says, and says better: the legal ball is the lit
          one, in colour, at thumb size. A caption repeating it in words was
          the same fact twice, and it cost half the width of the row.
          Three columns, because three numbers read across faster than they
          read down, and the block is now wide enough for the longest German
          caption to sit over its value without wrapping. */}
      <div
        className={`hud-frame hud-frame-sm flex shrink-0 items-stretch px-2 py-2.5 ${
          exhausted ? 'text-sys-yellow' : ''
        }`}
      >
        <Stat label={t('match.redsLabel')} value={state.redsRemaining}
          beforeValue={
            <button type="button" className="step-btn h-7 w-7 shrink-0 text-base"
              aria-label={t('match.redsDecrease')}
              disabled={illegalRedAdjustmentReason(state, -1) !== null}
              onClick={() => void dispatch({ type: 'reds.adjusted', source: 'manual', delta: -1 })}>−</button>
          }
          afterValue={
            <button type="button" className="step-btn h-7 w-7 shrink-0 text-base"
              aria-label={t('match.redsIncrease')}
              disabled={illegalRedAdjustmentReason(state, 1) !== null}
              onClick={() => void dispatch({ type: 'reds.adjusted', source: 'manual', delta: 1 })}>+</button>
          } />
        <span aria-hidden className="my-0.5 w-px shrink-0 bg-white/10" />
        <Stat label={t('match.remainingLabel')} value={state.pointsAvailable} />
        <span aria-hidden className="my-0.5 w-px shrink-0 bg-white/10" />
        {/* Who is ahead, and by how much — the number every player at the
            table is doing in their head between shots, and the one thing the
            remote knew and did not say. */}
        <Stat label={t('match.gapLabel')} value={Math.abs(state.scores[0] - state.scores[1])} />
      </div>

      {/* Four across, so every ball is a thumb-sized target on the narrowest
          phone worth supporting and the pad never needs a scroll. The pad is
          exactly as tall as two rows of balls: they are square, so any extra
          height it were given could only become padding around them. The
          slack on a tall phone goes to the score cards instead, where it
          becomes bigger numerals. */}
      <div className="hud-frame hud-frame-sm flex w-full shrink-0 flex-col gap-2 p-2">
        <div className="mx-auto grid w-full max-w-[24rem] grid-cols-4 gap-2">
          {(['red', ...COLORS] as BallColor[]).map((color) => {
            const onTable = isBallOnTable(state, color);
            const free = isFreeBallPot(state, color);
            const legal = onTable && canPot(state, color, 1);
            const gone = !onTable;
            const lit = legal && !exhausted;
            return (
              <button
                key={color}
                type="button"
                disabled={locked || !legal}
                onClick={() => pot(color, 1, free)}
                title={
                  free
                    ? t('match.freeBallWorth', { n: freeBallValue(state, color) })
                    : t(ballKey(color))
                }
                aria-label={`${t(ballKey(color))} ${
                  free ? freeBallValue(state, color) : BALL_VALUES[color]
                }`}
                className="ball-cell aspect-square w-full transition active:scale-95
                           disabled:cursor-not-allowed"
              >
                {/* Identical to the scoreboard's rack, down to the constants:
                    the gloss, the pale-ball ink, and — the point of this — no
                    white collar around the ball that is on. A hard ring drawn
                    around a sphere flattens its silhouette back into a
                    sticker, so being on is said with light instead, exactly as
                    it is on the board. Sizing the sphere's own font-size off
                    its width is what lets the numeral be 0.42em here and there
                    without either being a scaled photo of the other. */}
                <span
                  aria-hidden
                  style={{
                    ...BALL_GLOSS,
                    fontSize: '100cqw',
                    ...(lit
                      ? { filter: 'drop-shadow(0 0.05em 0.12em rgba(0,0,0,0.6)) brightness(1.06)' }
                      : null),
                  }}
                  className={[
                    'grid h-full w-full place-items-center rounded-full transition',
                    BALL_SWATCH[color],
                    lit ? '' : 'opacity-50 saturate-[0.7] brightness-[0.8]',
                    // Ringed in the award's own gold: this ball is about to
                    // score something other than the figure on it.
                    free ? 'ring-2 ring-sys-yellow/80' : '',
                  ].join(' ')}
                >
                  <span
                    className={`num text-[0.42em] font-semibold leading-none ${
                      PALE_BALLS.has(color) ? 'text-black/65' : 'text-white/90'
                    } ${gone && color !== 'red' ? 'line-through' : ''}`}
                    style={{
                      textShadow: PALE_BALLS.has(color)
                        ? '0 0.03em 0.05em rgba(255,255,255,0.45)'
                        : '0 0.03em 0.06em rgba(0,0,0,0.55)',
                    }}
                  >
                    {free ? freeBallValue(state, color) : BALL_VALUES[color]}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <TablePreview state={state} showPreview={showTablePreview} />
      {paused && live && (
        <p className="shrink-0 text-center text-xs text-sys-yellow">{pauseNotice}</p>
      )}
      {exhausted && live && !paused && (
        <p className="shrink-0 text-center text-xs text-sys-yellow">{t('match.exhausted')}</p>
      )}

      {/* Switching stays on the player cards; this row holds the other actions. */}
      <div className="hud-frame hud-frame-sm flex shrink-0 flex-col gap-2 p-2">
        <div className={`remote-action-buttons grid h-16 ${paused || freeBallOffered(state) ? 'grid-cols-5 gap-1' : 'grid-cols-3 gap-2'}`}>
          {(paused || freeBallOffered(state)) && (
            <div className="col-span-2 grid min-w-0">
              {paused ? <RestorationControls state={state} onChanged={refresh} disabled={saving} />
                : <FoulFollowUp state={state} disabled={!live}
                  onFreeBall={setFreeBall} onBack={() => void returnToOffender()} />}
            </div>
          )}
          <FoulButton
            disabled={!live || exhausted || (paused && state.pauseReason !== 'waiting_for_respot')}
            onFoul={points => void recordFoul(points)}
          />
          <button
            type="button"
            onClick={() => void undoLast()}
            className="btn-ghost flex flex-col items-center justify-center gap-1 px-1 py-2.5
                       text-xs font-semibold"
          >
            <UndoIcon className="h-5 w-5" />
            <span className="max-w-full truncate">{t('match.undo')}</span>
          </button>
          <button
            type="button"
            disabled={!live || saving}
            onClick={() => void finishFrame()}
            className={[
              'btn flex flex-col items-center justify-center gap-1 px-1 py-2.5',
              'text-xs font-semibold disabled:opacity-40',
              state.frameDecided
                ? 'bg-sys-green text-hud-bg'
                : 'bg-white/[0.08] ring-1 ring-inset ring-white/[0.16]',
            ].join(' ')}
          >
            <CrownIcon
              className={`h-5 w-5 ${state.frameDecided ? 'text-hud-bg' : 'text-sys-yellow'}`}
            />
            <span className="max-w-full truncate">{t('match.endFrame')}</span>
          </button>
        </div>
      </div>

      {frameWinnerRequired && (
        <FrameWinnerDialog players={state.players} disabled={saving} error={error}
          onSelect={player => void finishFrame(player)} onCancel={cancelFrameWinner} />
      )}
      {!live && (
        <p className="shrink-0 text-center text-xs text-white/50">
          {state.status === 'match_over'
            ? t('remote.matchOver')
            : t('remote.waitingNextFrame')}
        </p>
      )}
      {scoreEntry?.matchId === matchId && scoreEntry.frameNumber === state.frameNumber && live && (
        <ScoreEntryDialog
          playerName={state.players[scoreEntry.player]}
          disabled={paused}
          error={error}
          onClose={() => setScoreEntry(null)}
          onConfirm={async (points, clientId) => {
            const saved = await dispatch({ type: 'score.added', source: 'manual',
              player: scoreEntry.player, points, endTurn: true, clientId });
            if (saved) setScoreEntry(current => current === scoreEntry ? null : current);
            return saved;
          }}
        />
      )}
    </main>
  );
}

/**
 * The foul, and the four awards behind it.
 *
 * Four permanent keys were four of the largest targets on the remote given to
 * the rarest thing that happens on the table, sitting directly under the balls
 * — and a five-point foul tapped instead of a five-point blue is the one
 * mistake that costs points to both players at once. One key, then a choice,
 * makes a foul deliberate.
 *
 * The awards open upward, because this key is near the bottom of a phone and a
 * menu that opens downward opens off the screen. They are rendered into the
 * body rather than beside the key: the remote is a flex column with
 * `overflow-hidden`, so a menu left in place would be laid out as a fourth key
 * in the row and then clipped.
 */
function FoulButton({
  onFoul,
  disabled,
}: {
  onFoul: (points: number) => void;
  disabled: boolean;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState({ left: 0, bottom: 0 });
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open || !trigger.current) return;
    const r = trigger.current.getBoundingClientRect();
    setAt({ left: r.left + r.width / 2, bottom: window.innerHeight - r.top + 8 });
  }, [open]);

  // A menu that can only be closed by choosing from it is a trap: a foul
  // opened by mistake has to be as easy to abandon as it was to open.
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent): void => {
      const target = e.target as Node;
      if (!menu.current?.contains(target) && !trigger.current?.contains(target)) setOpen(false);
    };
    const escape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  return (
    <>
      {open &&
        createPortal(
          <div
            ref={menu}
            className="hud-frame fixed z-50 flex -translate-x-1/2 gap-1.5 p-2"
            style={{
              left: at.left,
              bottom: at.bottom,
              ['--hud-bright' as string]: 'rgba(255,105,97,0.5)',
              ['--hud-glow' as string]: 'transparent',
              ['--hud-fill' as string]: 'rgba(255,69,58,0.08)',
              ['--hud-c' as string]: '22px',
              ['--hud-edge' as string]: '6px',
            }}
          >
            {[4, 5, 6, 7].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => {
                  setOpen(false);
                  onFoul(n);
                }}
                className="num h-12 w-12 rounded-2xl bg-sys-red/15 text-xl font-semibold
                           leading-none text-[#ff6b61] ring-1 ring-inset ring-sys-red/40
                           transition active:scale-95"
                title={t('match.foul', { n })}
              >
                {n}
              </button>
            ))}
          </div>,
          document.body,
        )}

      <button
        ref={trigger}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={[
          'btn flex flex-col items-center justify-center gap-1 px-1 py-2.5',
          'text-xs font-semibold text-[#ff6b61] ring-1 ring-inset ring-sys-red/40',
          'disabled:opacity-40',
          open ? 'bg-sys-red/30' : 'bg-sys-red/[0.12]',
        ].join(' ')}
      >
        <FoulIcon className="h-5 w-5" />
        <span className="max-w-full truncate">{t('match.foulLabel')}</span>
      </button>
    </>
  );
}

/** One labelled number in the table-state block, kept on one line so the
 *  label and value read as one fact at a glance. */
function Stat({ label, value, beforeValue, afterValue }: {
  label: string;
  value: number;
  beforeValue?: ReactNode;
  afterValue?: ReactNode;
}) {
  return (
    <span data-remote-stat className="flex min-w-0 flex-1 items-center justify-between gap-2 px-0.5 max-sm:flex-col max-sm:justify-center max-sm:gap-1">
      <span className="text-[0.74rem] font-semibold leading-none tracking-[0.04em] text-white/72 max-sm:text-center max-sm:leading-tight">
        {label}
      </span>
      <span className="flex h-8 shrink-0 items-center justify-center gap-0.5">
        {beforeValue}
        <span data-remote-stat-value className="num min-w-5 shrink-0 whitespace-nowrap text-center text-[1.15rem] font-bold leading-none tabular-nums">
          {value}
        </span>
        {afterValue}
      </span>
    </span>
  );
}

/** Both scores, with the player at the table lit up. */
function ScoreLine({ state, onTakeTable, onAddScore }: {
  state: ScoreState;
  onTakeTable?: () => void;
  onAddScore: (player: PlayerIndex) => void;
}) {
  const t = useT();

  return (
    // Give the remaining viewport height to the two scrollable visit logs.
    <div className="grid min-h-[12rem] flex-1 grid-cols-2 gap-2">
      {([0, 1] as const).map((p) => {
        const atTable = state.activePlayer === p;
        const accent = accentOf(p);
        const canTake = !atTable && onTakeTable !== undefined;
        const calculator = (
          <button type="button" data-score-entry={p}
            aria-label={t('scoreEntry.open', { name: state.players[p] })}
            disabled={state.paused || state.status !== 'in_progress' || state.practice}
            onClick={() => onAddScore(p)}
            className="btn-ghost pointer-events-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-full p-1 text-white/90">
            <CalculatorIcon className="h-6 w-6" />
          </button>
        );
        return (
          // The same framed glass as the scoreboard's player panels, driven by
          // the same variables: the rim carries the colour and being at the
          // table is said by brightness. A hand-rolled inset ring here meant
          // the remote and the board disagreed about what "your turn" looks
          // like on two screens showing one match.
          <div
            key={p}
            data-player-panel={p}
            className={`hud-frame hud-frame-sm remote-score relative flex min-w-0 flex-col justify-center px-2 py-2
                        text-center transition
                        ${atTable ? '' : 'opacity-60'}`}
            style={frameVars(accent.rgb, atTable) as CSSProperties}
          >
            <button type="button" data-take-table={p} disabled={!canTake} aria-pressed={atTable}
              aria-label={atTable ? state.players[p] : t('match.takeTable', { name: state.players[p] })}
              onClick={onTakeTable}
              className="absolute inset-0 rounded-[inherit] focus-visible:outline focus-visible:outline-2
                         focus-visible:outline-offset-2 focus-visible:outline-white/70 disabled:cursor-default" />
            <div className="pointer-events-none relative flex w-full shrink-0 items-center justify-center gap-1">
              <span className="w-8 shrink-0">{p === 1 && calculator}</span>
              <span data-player-name title={state.players[p]}
                className="flex h-8 min-w-0 items-center text-sm font-semibold">
                <span className="truncate">{state.players[p]}</span>
              </span>
              <span className="w-8 shrink-0">{p === 0 && calculator}</span>
            </div>
            {/* Sized off the card rather than in points, and clamped at both
                ends: big enough to read across a table on a phone with room
                to spare, never so big that a three-digit score meets the rim
                on a short one. */}
            <span
              className="num pointer-events-none relative block shrink-0 font-black leading-none tabular-nums"
              style={{
                color: accent.hex,
                fontSize: 'clamp(2rem, min(32cqh, 40cqw), 5.5rem)',
              }}
            >
              {state.scores[p]}
            </span>
            {/* Fixed height whether or not there is a break to show, so the
                two cards stay the same size and neither jumps when the table
                changes hands. */}
            <span className="pointer-events-none relative mt-1 block h-4 shrink-0 text-xs font-bold" style={{ color: accent.hex }}>
              {atTable && t('match.break', { n: state.currentBreak })}
            </span>
            <div className={`relative mt-2 min-h-[3.5rem] flex-1 ${
              state.visits.some(visit => visit.player === p) ? '' : 'pointer-events-none'
            }`}>
              <BreakHistory visits={state.visits} player={p} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
